import {
  format,
  isValid,
  isDate,
  addDays,
  addYears,
  addWeeks,
  subDays,
  addHours,
  addMonths,
  subMonths,
  subWeeks,
  isAfter as isAfterDate,
  startOfWeek as sow,
  isBefore as isBeforeDate,
  isEqual,
  setMonth as setMonthToDate,
  setDate as setDayOfMonth,
  setDay as setDayOfWeek,
  parse
} from "date-fns";
import moment from "moment";
const isMoment = moment.isMoment;

/**
 * Returns the new date with the days added
 * @param {Date} date - The date to be changed
 * @param {Number} amount - The amount of days to be added
 * @returns {Date} The new date with the days added
 */
export function addDaysToDate(date, amount) {
  if (isMoment(date)) {
    return addDays(date.toDate(), amount);
  }
  const d = new Date(date);
  return isDate(d) && isValid(d) ? addDays(d, amount) : date;
}

/**
 * Returns the new date with the hours added
 * @param {Date} date - The date to be changed
 * @param {Number} amount - The amount of hours to be added
 * @returns {Date} The new date with the hours added
 */
export function addHoursToDate(date, amount) {
  if (isMoment(date)) {
    return addHours(date.toDate(), amount);
  }
  const d = new Date(date);
  return isDate(d) && isValid(d) ? addHours(d, amount) : date;
}

/**
 * Returns the new date with the months added
 * @param {Date} date - The date to be changed
 * @param {Number} amount - The amount of months to be added
 * @returns {Date} The new date with the months added
 */
export function addMonthsToDate(date, amount) {
  if (isMoment(date)) {
    return addMonths(date.toDate(), amount);
  }
  const d = new Date(date);
  return isDate(d) && isValid(d) ? addMonths(d, amount) : date;
}

/**
 * Returns the new date with the weeks added
 * @param {Date} date - The date to be changed
 * @param {Number} amount - The amount of weeks to be added
 * @returns {Date} The new date with the weeks added
 */
export function addWeeksToDate(date, amount) {
  if (isMoment(date)) {
    return addWeeks(date.toDate(), amount);
  }
  const d = new Date(date);
  return isDate(d) && isValid(d) ? addWeeks(d, amount) : date;
}

/**
 * Returns the new date with the years added
 * @param {Date} date - The date to be changed
 * @param {Number} amount - The amount of years to be added
 * @returns {Date} The new date with the years added
 */
export function addYearsToDate(date, amount) {
  if (isMoment(date)) {
    return addYears(date.toDate(), amount);
  }
  const d = new Date(date);
  return isDate(d) && isValid(d) ? addYears(d, amount) : date;
}

/**
 * Returns the formatted date string
 * @param {Date} date - The date to be formatted
 * @param {string} formatString - A string of accepted tokens.
 * To see a list of accepted tokens visit {@link https://date-fns.org/v2.16.1/docs/format}
 * @returns {string}  Formatted date string
 */
export function formatDate(date, formatString) {
  if (isMoment(date)) {
    const dt = date.toDate();
    return format(dt, formatString);
  }
  const d = new Date(date);
  return isDate(d) && isValid(d) ? format(d, formatString) : "";
}

/**
 * Returns the new date with the days subtracted
 * @param {Date} date - The date to be changed
 * @param {Number} amount - The amount of days to be subtracted
 * @returns {Date} The new date with the days subtracted
 */
export function subtractDays(date, amount) {
  if (isMoment(date)) {
    return subDays(date.toDate(), amount);
  }
  const d = new Date(date);
  return isDate(d) && isValid(d) ? subDays(d, amount) : date;
}

/**
 * Returns the new date with the months subtracted
 * @param {Date} date - The date to be changed
 * @param {Number} amount - The amount of months to be subtracted
 * @returns {Date} The new date with the months subtracted
 */
export function subtractMonths(date, amount) {
  if (isMoment(date)) {
    return subMonths(date.toDate(), amount);
  }
  const d = new Date(date);
  return isDate(d) && isValid(d) ? subMonths(d, amount) : date;
}

/**
 * Returns the new date with the weeks subtracted
 * @param {Date} date - The date to be changed
 * @param {Number} amount - The amount of weeks to be subtracted
 * @returns {Date} The new date with the weeks subtracted
 */
export function subtractWeeks(date, amount) {
  if (isMoment(date)) {
    return subWeeks(date.toDate(), amount);
  }
  const d = new Date(date);
  return isDate(d) && isValid(d) ? subWeeks(d, amount) : date;
}

/**
 * Returns the number of days between two dates
 * @param {Date} date1 - The start date
 * @returns {Date} date2 - The end date that is today as default
 */
export function getDifferenceBetweenDates(
  date1,
  date2 = new Date(),
  absolute = false
) {
  const difference = date1.getTime() - date2.getTime();
  const totalDays = Math.ceil(difference / (1000 * 3600 * 24));
  return absolute ? Math.abs(totalDays) : totalDays;
}

export function getDifferenceBetweenDatesSpecific(date1, date2) {
  const diffTime = new Date(date1).valueOf() - new Date(date2).valueOf();
  const days = Math.abs(diffTime / (24 * 60 * 60 * 1000));
  const hours = Math.abs((days % 1) * 24);
  const minutes = Math.abs((hours % 1) * 60);
  const result = {
    days: Math.floor(days),
    hours: Math.floor(hours),
    minutes: Math.floor(minutes)
  };
  return `${diffTime < 0 ? "-" : ""}${result.days ? `${result.days}d ` : ""} ${
    result.hours ? `${result.hours}h ` : ""
  } ${result.minutes ? `${result.minutes}m ` : ""}`;
}

/**
 * Returns the start of a week for the given date
 * @param {Date} date - The original date
 * @returns {Date} The start of a week
 */
export function startOfWeek(date) {
  return isValid(date) && isDate(date) ? sow(date) : null;
}

/**
 * Checks if the first date is after or equal than the second one
 * @param {Date} dateLeft - The date that should be after or equal than the other one to return true
 * @param {Date} dateRight - The date to compare to
 * @returns {Boolean} The first date is after or eqaul than the second date
 */
export function isSameOrAfter(dateLeft, dateRight) {
  return isEqual(dateLeft, dateRight) || isAfterDate(dateLeft, dateRight);
}

/**
 * Checks if the first date is before or equal than the second one
 * @param {Date} dateLeft - The date that should be before or equal than the other one to return true
 * @param {Date} dateRight - The date to compare to
 * @returns {Boolean} The first date is before or eqaul than the second date
 */
export function isSameOrBefore(dateLeft, dateRight) {
  return isEqual(dateLeft, dateRight) || isBeforeDate(dateLeft, dateRight);
}

/**
 * Set the month to the given date
 * @param {Date} date - The date to be changed
 * @param {String|Number} month - The month of the new date
 * @returns {Date} The new date with the month set
 */
export function setMonth(date, month) {
  if (typeof month === "number") {
    return setMonthToDate(date, month);
  }
  const parseString = month.length === 3 ? "MMM" : "MMMM";
  const tempDate = parse(month, parseString, new Date());
  return setMonthToDate(date, tempDate.getMonth());
}

/**
 * Set the day of the month to the given date.
 * @param {Date} date - The date to be changed
 * @param {Number} dayOfMonth - The day of the month of the new date
 * @returns {Date} The new date with the day of the month set
 */
export function setDate(date, dayOfMonth) {
  return setDayOfMonth(date, dayOfMonth);
}

/**
 * Set the day of the week to the given date.
 * @param {Date} date - The date to be changed
 * @param {Number} day - The day of the week of the new date
 * @returns {Date} The new date with the day of the week set
 */
export function setDay(date, day) {
  return setDayOfWeek(date, day);
}

/**
 * Returns the new date with the amount of the specified units added
 * @param {Date} date - The date to be changed
 * @param {Number} amount - The amount of units to be added
 * @param {String} unit - The unit of time to add
 * @returns {Date} The new date with the amount of the specified units added
 */
export function addUnitsToDate(date, amount, unit) {
  switch (unit) {
    case "day":
      return addDays(date, amount);
    case "month":
      return addMonths(date, amount);
    case "week": {
      return addWeeks(date, amount);
    }
    case "year":
      return addYears(date, amount);
    default:
      return date;
  }
}
